import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import type { ActionFunctionArgs, LinksFunction, LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { data } from '@remix-run/node'
import type { ShouldRevalidateFunction } from '@remix-run/react'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useMatches,
	useNavigation,
	useRouteError,
	useRouteLoaderData,
} from '@remix-run/react'
import type { SentryMetaArgs } from '@sentry/remix'
import * as Sentry from '@sentry/remix'
import { withSentry } from '@sentry/remix'
import clsx from 'clsx'
import NProgress from 'nprogress'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { toast as sonnerToast, Toaster } from 'sonner'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { CommonErrorBoundary } from './components/error-boundary'
import { CookieBanner } from './routes/_main+/_rest+/cookies.js'
import type { InsightsPageTracking } from './routes/api+/insights.$.js'
import type { SitemapEntry } from './routes/sitemap[.xml].js'
import fontStylestyleSheetUrl from './styles/font.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getSessionUser } from './utils/auth.server'
import { ClientHintCheck, getHints } from './utils/client-hints'
import { getCookiePreferencesSessionData } from './utils/cookie-preferences.server.js'
import { getClientEnv } from './utils/env.server'
import { useDebounce } from './utils/misc.js'
import { getDomainUrl } from './utils/misc.server'
import { useNonce } from './utils/nonce-provider'
import { useTheme } from './utils/theme.js'
import { getTheme } from './utils/theme.server.js'
import { getToast } from './utils/toast.server.js'
import { umami } from './utils/umami.client.js'

export type Handle = {
	htmlProps?: JSX.IntrinsicElements['html']
	bodyProps?: JSX.IntrinsicElements['body']
	getSitemapEntries?: (request: Request) => Promise<SitemapEntry[]> | SitemapEntry[]
	getPageTracking?: (request: Request) => Promise<InsightsPageTracking>
}

export const shouldRevalidate: ShouldRevalidateFunction = ({ actionStatus, defaultShouldRevalidate }) => {
	return actionStatus != null || defaultShouldRevalidate
}

export const links: LinksFunction = () => {
	return [
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{ rel: 'manifest', href: '/site.webmanifest' },
		{ rel: 'preload', href: fontStylestyleSheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: fontStylestyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta = ({ data, error }: SentryMetaArgs<MetaFunction<typeof loader>>) => {
	return [
		{ title: !error ? 'GradeGuide' : 'Uh oh! - GradeGuide' },
		{
			name: 'description',
			content: 'Your personal university grade calculator',
		},
		{
			name: 'sentry-trace',
			content: data?.sentryTrace,
		},
		{
			name: 'baggage',
			content: data?.sentryBaggage,
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs | ActionFunctionArgs) {
	const user = await getSessionUser({ request })
	const { toast, headers } = await getToast(request)

	const sentryTrace = request.headers.get('sentry-trace')
	const sentryBaggage = request.headers.get('baggage')

	return data(
		{
			user,
			toast,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: { theme: getTheme(request) },
				cookiePrefs: await getCookiePreferencesSessionData({ request }),
			},
			env: getClientEnv(),
			sentryTrace,
			sentryBaggage,
		},
		{ headers },
	)
}

function App() {
	return <Outlet />
}

export function Layout({ children }: { children?: ReactNode }) {
	const matches = useMatches()

	const data = useRouteLoaderData<typeof loader>('root')
	const error = useRouteError()

	const nonce = useNonce()

	const htmlProps = matches.map((m) => (m.handle as Handle)?.htmlProps).filter(Boolean)
	const bodyProps = matches.map((m) => (m.handle as Handle)?.bodyProps).filter(Boolean)

	const htmlClassNames = htmlProps.map((p) => p.className).filter(Boolean)
	const bodyClassNames = bodyProps.map((p) => p.className).filter(Boolean)

	const mergedHtmlProps = htmlProps.reduce((a, p) => ({ ...a, ...p }), {})
	const mergedBodyProps = bodyProps.reduce((a, p) => ({ ...a, ...p }), {})

	const finalHtmlProps = error ? {} : mergedHtmlProps
	const finalBodyProps = error ? {} : mergedBodyProps

	const finalHtmlClassNames = error ? [] : htmlClassNames
	const finalBodyClassNames = error ? [] : bodyClassNames

	// For custom dark mode
	const theme = useTheme()

	const toast = data?.toast
	useEffect(() => {
		if (!toast) return
		sonnerToast[toast.type](toast.message, { description: toast.description, duration: toast.duration })
	}, [toast])

	const navigation = useNavigation()
	const fetchers = useFetchers()

	const derivedState = [navigation, ...fetchers].find((f) => f.state === 'submitting')
		? 'submitting'
		: [navigation, ...fetchers].find((f) => f.state === 'loading')
			? 'loading'
			: 'idle'
	const active = derivedState !== 'idle'
	const [activeDebounced] = useDebounce(active, 500)

	useEffect(() => {
		NProgress.configure({
			template:
				'<div class="bar h-0.5 bg-blue-500 fixed top-0 left-0 right-0" role="bar"><div class="peg"></div></div>',
			trickleSpeed: 500,
		})

		if (activeDebounced && active) {
			NProgress.start()
		} else {
			NProgress.done()
		}
	}, [active, activeDebounced])

	const userId = data?.user?.id
	useEffect(() => {
		if (userId) {
			umami.identify(userId, {})
			Sentry.setUser({ id: userId })
		} else {
			umami.identify(undefined, {})
			Sentry.setUser(null)
		}
	}, [userId])

	return (
		<html
			lang="en"
			{...finalHtmlProps}
			className={clsx('overflow-y-scroll antialiased', theme, ...finalHtmlClassNames)}
		>
			<head>
				<ClientHintCheck nonce={nonce} />

				<Meta />

				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<meta name="theme-color" content={theme === 'dark' ? '#18181b' : '#ffffff'} />

				<Links />
			</head>

			<body
				{...finalBodyProps}
				className={clsx('bg-white text-zinc-950 dark:bg-zinc-900 dark:text-white', finalBodyClassNames)}
			>
				{children}

				<Toaster
					position="top-right"
					toastOptions={{
						unstyled: true,
						classNames: {
							toast:
								'flex w-full gap-2.5 items-center bg-white px-5 py-4 rounded-lg shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-900 dark:ring-white/10',
							title: 'text-base/6 sm:text-sm/6 mt-px',
							description: 'text-sm text-zinc-500 mt-1.5 dark:text-zinc-400',
							icon: 'flex items-center mt-px text-blue-500',
						},
					}}
					icons={{
						success: <CheckCircleIcon className="size-6 sm:size-5" />,
						warning: <ExclamationCircleIcon className="size-6 sm:size-5" />,
						error: <XCircleIcon className="size-6 sm:size-5" />,
						loading: <ClockIcon className="size-6 sm:size-5" />,
					}}
				/>
				<CookieBanner />

				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />

				{data ? (
					<script nonce={nonce} dangerouslySetInnerHTML={{ __html: `window.env = ${JSON.stringify(data.env)}` }} />
				) : null}

				{data?.env.UMAMI_WEBSITE_ID && data?.requestInfo.cookiePrefs?.measureWebsiteUse ? (
					<UmamiScript websiteId={data.env.UMAMI_WEBSITE_ID} nonce={nonce} />
				) : null}
			</body>
		</html>
	)
}

export function ErrorBoundary() {
	return (
		<div className="flex min-h-svh flex-col">
			<CommonErrorBoundary />
		</div>
	)
}

function UmamiScript({ websiteId, nonce }: { websiteId: string; nonce: string }) {
	useEffect(() => {
		// Enable tracking by removing the disabled flag
		localStorage.removeItem('umami.disabled')

		// Only add the script if it doesn't exist
		if (!document.querySelector('script[src="/api/insights/script.js"]')) {
			const script = document.createElement('script')
			script.nonce = nonce
			script.defer = true
			script.crossOrigin = 'anonymous'
			script.src = '/api/insights/script.js'
			script.setAttribute('data-host-url', '/api/insights')
			script.setAttribute('data-website-id', websiteId)
			document.head.appendChild(script)
		}

		return () => {
			localStorage.setItem('umami.disabled', '1')
		}
	}, [websiteId, nonce])

	return null
}

export default withSentry(App)
